import { useTimeZone } from "#contexts/TimeZoneContext.ts";
import { TZDate } from "@date-fns/tz";
import { format, formatRelative, isToday } from "date-fns";
import { enUS, frCA } from "date-fns/locale";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface FormatDateOptions {
    showRelativeToday?: boolean;
    hideTime?: boolean;
    forceUTC?: boolean;
}

export const useFormatDate = () => {
    const timeZone = useTimeZone();
    const { i18n } = useTranslation();

    const locale = useMemo(() => i18n.language === "fr-CA" ? frCA : enUS, [i18n.language]);

    return useCallback((dateString: string | null | undefined, { showRelativeToday, hideTime, forceUTC }: FormatDateOptions = {}) => {
        if (!dateString) {
            return null;
        }

        const date = new Date(dateString);

        // `format` throws a RangeError on an unparseable date. A source is third-party data, so an
        // unexpected date shape must degrade to "no date" rather than take the caller down.
        if (Number.isNaN(date.getTime())) {
            return null;
        }

        if (showRelativeToday && isToday(date)) {
            return formatRelative(date, new Date(), { locale });
        }

        const dateFormat = hideTime ? "PP" : "PPp";

        const usedTimeZone = forceUTC ? "UTC" : timeZone;
        const zonedDate = new TZDate(date, usedTimeZone);

        return format(zonedDate, dateFormat, { locale });
    }, [locale, timeZone]);
};
